/**
 * CRM Filter Bar
 *
 * Filter controls for the pipeline board
 */

import { useState, useEffect } from 'react';
import { X, Search, Calendar, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { OPPORTUNITY_TYPES, DEFAULT_CURRENCY } from '../../constants';
import { useAccountManagers } from '../../hooks/useCrmPipeline';
import type { PipelineFilters, OpportunityType } from '../../types';

interface CrmFilterBarProps {
  filters: PipelineFilters;
  onFilterChange: <K extends keyof PipelineFilters>(
    key: K,
    value: PipelineFilters[K] | undefined
  ) => void;
  onClearFilters: () => void;
  isVisible: boolean;
}

export function CrmFilterBar({
  filters,
  onFilterChange,
  onClearFilters,
  isVisible,
}: CrmFilterBarProps) {
  const { data: accountManagers = [], isLoading: isLoadingManagers } = useAccountManagers();

  // Debounced search state (300ms delay)
  const [searchInput, setSearchInput] = useState(filters.search || '');

  // Sync searchInput with filters.search when filters change externally
  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  // Debounce the search filter update
  useEffect(() => {
    const timer = setTimeout(() => {
      onFilterChange('search', searchInput || undefined);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput, onFilterChange]);

  // Validate min/max value range
  const hasInvalidValueRange =
    filters.min_value !== undefined &&
    filters.max_value !== undefined &&
    filters.min_value > filters.max_value;

  if (!isVisible) return null;

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-3">
      <div className="flex items-center gap-4 flex-wrap">
        {/* Search */}
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search opportunities..."
            aria-label="Search opportunities"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Type Filter */}
        <Select
          value={filters.type || 'all'}
          onValueChange={(value) =>
            onFilterChange('type', value === 'all' ? undefined : (value as OpportunityType))
          }
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {OPPORTUNITY_TYPES.map((type) => (
              <SelectItem key={type.key} value={type.key}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Account Manager Filter */}
        <Select
          value={filters.account_manager || 'all'}
          onValueChange={(value) =>
            onFilterChange('account_manager', value === 'all' ? undefined : value)
          }
          disabled={isLoadingManagers}
        >
          <SelectTrigger className="w-48">
            <User className="h-4 w-4 mr-2 text-gray-400" />
            <SelectValue placeholder="All Managers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Managers</SelectItem>
            {accountManagers.map((manager) => (
              <SelectItem key={manager.id} value={manager.name}>
                {manager.name}{manager.isFormer ? ' (Former)' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Date Range - From */}
        <div className="relative w-36">
          <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            type="date"
            placeholder="From date"
            aria-label="Filter from date"
            value={filters.date_from || ''}
            onChange={(e) => onFilterChange('date_from', e.target.value || undefined)}
            className="pl-9"
          />
        </div>

        {/* Date Range - To */}
        <div className="relative w-36">
          <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            type="date"
            placeholder="To date"
            aria-label="Filter to date"
            value={filters.date_to || ''}
            onChange={(e) => onFilterChange('date_to', e.target.value || undefined)}
            className="pl-9"
          />
        </div>

        {/* Min Value */}
        <div className="relative w-32">
          <Input
            type="number"
            placeholder="Min value"
            aria-label="Minimum opportunity value"
            value={filters.min_value ?? ''}
            onChange={(e) =>
              onFilterChange('min_value', e.target.value ? Number(e.target.value) : undefined)
            }
            className="pr-8"
          />
          <span className="absolute right-2.5 top-2.5 text-xs text-gray-400">{DEFAULT_CURRENCY}</span>
        </div>

        {/* Max Value */}
        <div className="relative w-32">
          <Input
            type="number"
            placeholder="Max value"
            aria-label="Maximum opportunity value"
            value={filters.max_value ?? ''}
            onChange={(e) =>
              onFilterChange('max_value', e.target.value ? Number(e.target.value) : undefined)
            }
            className="pr-8"
          />
          <span className="absolute right-2.5 top-2.5 text-xs text-gray-400">{DEFAULT_CURRENCY}</span>
        </div>

        {/* Clear Filters */}
        <Button variant="ghost" size="sm" onClick={onClearFilters}>
          <X className="h-4 w-4 mr-1" />
          Clear
        </Button>
      </div>

      {/* Validation Error */}
      {hasInvalidValueRange && (
        <div className="mt-2 text-sm text-red-600 flex items-center gap-1">
          <X className="h-4 w-4" />
          Minimum value cannot be greater than maximum value
        </div>
      )}
    </div>
  );
}
